import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import PageHero from "@/components/PageHero";
import { PrimaryCTA } from "@/components/CTAButtons";

const CASES = [
  {
    tag: "Procurement consolidation",
    title:
      "A multi-site nutraceutical brand consolidated three regional suppliers into a single verified source.",
    context:
      "A mid-sized supplement brand was running three regional botanical extract suppliers with diverging specs, inconsistent COAs, and rising overhead in QA. They asked FFIB to evaluate consolidation without compromising formulation continuity.",
    approach:
      "FFIB analysts ran the requirement against the IngredientSignal intelligence base, narrowing the global field to four candidates verified through conference relationships and on-the-ground checks.",
    outcome:
      "A two-supplier strategy — one primary, one validated secondary — replaced the original three. The brand reduced sourcing surface area while improving spec consistency, and FFIB hosted the introductions and supported initial commercial discussions.",
    metric: "3 → 2 suppliers",
    metric_label: "Consolidated sourcing footprint",
  },
  {
    tag: "Supplier market entry",
    title:
      "A specialty ingredient manufacturer entered a new geography through targeted buyer introductions — not cold outreach.",
    context:
      "An established functional ingredient supplier wanted to enter a new regional market. Cold outreach attempts had stalled; their offer was strong but their relationships were thin.",
    approach:
      "IngredientSignal positioned the supplier inside FFIB's active buyer programs in the region, producing a focused buyer opportunity brief and curating a list of qualified procurement contacts.",
    outcome:
      "Five warm introductions were hosted within a single quarter, two of which advanced to sampling. The supplier shortened their market entry cycle materially without diluting their brand through bulk outreach.",
    metric: "5 warm intros",
    metric_label: "Hosted in one quarter",
  },
  {
    tag: "Verification-led sourcing",
    title:
      "A contract manufacturer avoided a high-risk supplier through FFIB's verification network.",
    context:
      "A contract manufacturer was preparing to onboard a new ingredient source for a flagship customer program. The supplier looked credible on paper but had limited reference points outside their home market.",
    approach:
      "FFIB ran the counterparty through the IngredientSignal verification process — cross-referencing conference signals, audit history, and informal industry feedback collected in person.",
    outcome:
      "Material concerns surfaced before onboarding. FFIB then proposed two alternative suppliers from the intelligence base — both already known and verified — and managed the warm introductions.",
    metric: "0 risk events",
    metric_label: "Avoided onboarding risk",
  },
];

export default function CaseStudies() {
  const reduce = useReducedMotion();
  return (
    <div data-testid="case-studies-page">
      <PageHero
        eyebrow="Case Studies"
        title={
          <>
            Outcomes from
            <br />
            <span className="text-brand-olive">intelligence-led sourcing</span>.
          </>
        }
        subtitle="Three representative engagements showing how IngredientSignal moves from sourcing question to verified action — managed end-to-end by FFIB."
      />

      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-px bg-brand-border">
          {CASES.map((c, idx) => (
            <motion.article
              key={c.tag}
              data-testid={`case-study-${idx}`}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="bg-brand-cream p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10"
            >
              <div className="lg:col-span-4">
                <div className="text-xs font-semibold tracking-[0.24em] uppercase text-brand-olive">
                  Case · {String(idx + 1).padStart(2, "0")}
                </div>
                <p className="mt-4 text-xs font-mono tracking-wide text-brand-quiet uppercase">
                  {c.tag}
                </p>
                <div className="mt-10 pt-10 border-t border-brand-border">
                  <div className="font-display text-3xl text-brand-olive font-light">
                    {c.metric}
                  </div>
                  <p className="mt-2 text-sm text-brand-quiet font-light">
                    {c.metric_label}
                  </p>
                </div>
              </div>
              <div className="lg:col-span-8">
                <h3 className="font-display text-2xl md:text-3xl text-brand-deep font-light leading-snug">
                  {c.title}
                </h3>
                <div className="mt-10 space-y-8">
                  {[
                    ["Context", c.context],
                    ["Approach", c.approach],
                    ["Outcome", c.outcome],
                  ].map(([label, text]) => (
                    <div key={label}>
                      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-brand-quiet">
                        {label}
                      </div>
                      <p className="mt-3 text-brand-warm leading-relaxed font-light">
                        {text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="py-24 border-t border-brand-border bg-brand-linen/70">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-3xl sm:text-4xl text-brand-deep font-light leading-tight">
            Bring us your scenario.
          </h2>
          <p className="mt-5 text-brand-muted max-w-2xl font-light">
            Every engagement starts with a short intake. FFIB will respond with
            the right deliverable for your situation.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 items-center">
            <PrimaryCTA to="/get-started" testId="case-studies-bottom-cta">
              Get Started
            </PrimaryCTA>
            <ArrowRight className="hidden sm:block w-4 h-4 text-brand-quiet" />
            <span className="text-sm text-brand-quiet font-mono">
              avg. response &lt; 2 business days
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
